import React from 'react';
import type { Notification } from '../../types/notifications';
import { useNotifications } from '../../contexts/NotificationContext';

interface NotificationItemProps {
  notification: Notification;
  compact?: boolean;
  onActionClick?: () => void;
}

const categoryIcons: Record<string, JSX.Element> = {
  Evidence: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Case: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  ),
  LegalHold: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  ),
  Discovery: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  ),
  Compliance: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  System: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

const priorityColors: Record<string, string> = {
  Critical: 'bg-red-100 text-red-700 border-red-300',
  High: 'bg-orange-100 text-orange-700 border-orange-300',
  Medium: 'bg-blue-100 text-blue-700 border-blue-300',
  Low: 'bg-gray-100 text-gray-600 border-gray-300',
};

const priorityIconColors: Record<string, string> = {
  Critical: 'bg-red-600 text-white',
  High: 'bg-orange-500 text-white',
  Medium: 'bg-blue-500 text-white',
  Low: 'bg-gray-400 text-white',
};

function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  compact = false,
  onActionClick 
}) => {
  const { markAsRead, acknowledge, dismiss } = useNotifications();

  const isUnread = notification.state === 'Sent' || notification.state === 'Delivered';
  const isCritical = notification.priority === 'Critical';
  const needsAcknowledgment = isCritical && notification.state !== 'Acknowledged';
  const category = notification.category || 'System';
  const priority = notification.priority || 'Medium';

  const handleClick = () => {
    if (isUnread) {
      markAsRead(notification.id);
    }
    if (onActionClick) {
      onActionClick();
    }
  };

  const handleAcknowledge = (e: React.MouseEvent) => {
    e.stopPropagation();
    acknowledge(notification.id);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismiss(notification.id);
  };

  if (compact) {
    return (
      <div
        onClick={handleClick}
        className={`flex items-start gap-3 p-3 cursor-pointer transition-colors hover:bg-gray-50 ${
          isUnread ? 'bg-blue-50/50' : ''
        } ${isCritical ? 'border-l-4 border-red-500' : ''}`}
      >
        {/* Icon */}
        <div className={`flex-shrink-0 p-1.5 rounded-full ${priorityIconColors[priority] || priorityIconColors.Medium}`}>
          {categoryIcons[category] || categoryIcons.System}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-700'} line-clamp-1`}>
              {notification.title}
            </p>
            <span className="flex-shrink-0 text-xs text-gray-500">
              {formatRelativeTime(notification.createdAt)}
            </span>
          </div>
          <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">{notification.body}</p>
          
          {/* Quick actions for critical */}
          {needsAcknowledgment && (
            <button
              onClick={handleAcknowledge}
              className="mt-2 px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
            >
              Acknowledge
            </button>
          )}
        </div>

        {/* Unread indicator */}
        {isUnread && (
          <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-600" />
        )}
      </div>
    );
  }

  // Full view
  return (
    <div 
      className={`p-4 rounded-lg border ${
        isUnread ? 'bg-white shadow-sm' : 'bg-gray-50'
      } ${isCritical ? 'border-red-300 ring-1 ring-red-100' : 'border-gray-200'}`}
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`flex-shrink-0 p-2 rounded-lg ${priorityIconColors[priority] || priorityIconColors.Medium}`}>
          {categoryIcons[category] || categoryIcons.System}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className={`text-sm ${isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                {notification.title}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${priorityColors[priority] || priorityColors.Medium}`}>
                  {notification.priority}
                </span>
                <span className="text-xs text-gray-500">{notification.category}</span>
                {notification.caseNumber && (
                  <span className="text-xs text-gray-500">• Case: {notification.caseNumber}</span>
                )}
              </div>
            </div>
            <span className="flex-shrink-0 text-xs text-gray-500">
              {formatRelativeTime(notification.createdAt)}
            </span>
          </div>

          <p className="mt-2 text-sm text-gray-600">{notification.body}</p>

          {/* Triggered by */}
          {notification.triggeredBy && (
            <p className="mt-2 text-xs text-gray-500">
              By: {notification.triggeredBy.name} ({notification.triggeredBy.role})
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3">
            {notification.actionUrl && (
              <a
                href={notification.actionUrl}
                onClick={handleClick}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
              >
                {notification.actionLabel || 'View'}
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            )}
            {needsAcknowledgment && (
              <button
                onClick={handleAcknowledge}
                className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
              >
                Acknowledge
              </button>
            )}
            {!isCritical && (
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
